import React, { Component, ReactNode } from 'react';
import './Login.scss';
import { AuthLayout } from '../layouts/auth-layout/AuthLayout';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { PasswordUpdateForm } from '../features/password-update-form/PasswordUpdateForm';
import { LoginForm } from '../features/login-form/LoginForm';
import { AcceptPoliciesChallenge, AuthChallenge, AuthToken, MfaSetupPhoneChallenge, MfaSmsChallenge, MfaVerifyPhoneChallenge, NewPasswordChallenge } from '../helpers/AuthHelper';
import Spinner from 'react-bootstrap/Spinner';
import { UserHelper } from '../helpers/UserHelper';
import { UserAccountRoleEnum } from '../api/types';
import { ERouterPath } from '../features/router/ERouterPath';
import { connect } from 'react-redux';
import { Api } from '../api';
import { registerPushService } from '../serviceWorker';
import { sessionActions } from '../app/session/actions';
import { DossierV2 } from '../models/DossierV2';
import { store } from '../app/store';
import { DossierMember } from '../models/DossierMember';
import { MfaAuthForm } from '../features/mfa-auth-form/MfaAuthForm';
import { SetUserPhone } from '../features/set-user-phone-form/SetUserPhoneForm';
import { Alert } from 'react-bootstrap';
import { AcceptPoliciesForm } from '../features/accept-policies-form/AcceptPoliciesForm';

interface LoginProps extends RouteComponentProps {
  setActiveDossier: (dossier: DossierV2, dossierRelations: DossierMember[], relation: DossierMember) => void;
  clearActiveDossier: () => void;
}

interface LoginState {
  loginError?: string;
  authChallenge?: AuthChallenge;
  firstTimeLogin?: boolean;
  loading?: boolean;
}

class Login extends Component<LoginProps, LoginState> {
  constructor(props: LoginProps) {
    super(props);
    this.state = {
    };
  }

  handleLogin = (session?: AuthToken|AuthChallenge): void => {
    if (session instanceof AuthToken) {
      this.setLoading();
      this.setState({ loginError: undefined });
      UserHelper.fetchAndStoreUser()
        .then((user) => {
          this.setLoadingDone();
          const { firstTimeLogin } = this.state;
          const hasMultipleRoles = store.getState().session.isMultiDossierUser;
          if (hasMultipleRoles &&
              user.accountRole !== UserAccountRoleEnum.caregiver &&
              user.accountRole !== UserAccountRoleEnum.administration &&
              user.accountRole !== UserAccountRoleEnum.adminCaregiver) {
            const queryParams = firstTimeLogin ? '?firstTimeLogin' : '';
            this.props.history.push(`${ERouterPath.selectFile}${queryParams}`);
            return undefined;
          }
          return Api.listUserDossiersWithUserRelation(user);
        })
        /*
           Now we are trying to register to the push service. This call below will throw an error if it fails.
           Failing is not an issue since the user might have chosen to deny notifications or the browser does
           not support notifications. Therefore this error is ignored.
        */
        .then(dossiersWithRelation => registerPushService().catch(() => undefined).then(() => dossiersWithRelation))
        .then((dossiersWithRelation) => {
          if (dossiersWithRelation) {
            if (dossiersWithRelation.length === 1) {
              const [{ dossier, dossierRelations, relation }] = dossiersWithRelation;
              this.props.setActiveDossier(dossier, dossierRelations, relation);
            }

            const { firstTimeLogin } = this.state;
            if (firstTimeLogin) {
              this.props.history.push(ERouterPath.accountSettings);
            } else {
              this.props.history.push(ERouterPath.dossier);
            }
          }
        })
        .catch(err => {
          this.setLoadingDone();
          if (err.key === 'InvalidCognitoTokenError') {
            this.setState({ loginError: 'Gebruiker heeft geen toegang tot het systeem' });
          } else if (err.key === 'MissingMfaForCognitoTokenError') {
            this.setState({ loginError: 'Toegang geweigerd doordat de gebruiker geen tweestapsverificatie aan heeft staan' });
          } else {
            this.setState({ loginError: 'Er heeft zich een onverwachte fout voor gedaan. Probeer het later opnieuw.' });
            console.error('An unexpected error while retrieving the user token occurred.', err);
          }
        });
    } else if (session instanceof NewPasswordChallenge) {
      console.warn('Received session to set a new password for the user');
      this.setState({ authChallenge: session, firstTimeLogin: true });
    } else {
      this.setState({ authChallenge: session });
    }
  };

  private setLoading = () => this.setState({ loading: true });
  private setLoadingDone = () => this.setState({ loading: false });

  handleReturn = (): void => {
    this.setState({ authChallenge: undefined, loginError: undefined });
  };

  private renderLoadingOverlay(): JSX.Element {
    const { loading } = this.state;
    if (loading) {
      return <div className="login__loading-overlay">
        <Spinner animation="border" role="status" variant="primary" />
      </div>;
    } else {
      return <></>;
    }
  }

  private renderErrorMessage(): JSX.Element {
    const { loginError } = this.state;
    if (loginError) {
      return <Alert variant="danger">{loginError}</Alert>;
    } else {
      return <></>;
    }
  }

  handleChangePhone = (authChallenge: MfaVerifyPhoneChallenge): void => {
    this.setState({ authChallenge: new MfaSetupPhoneChallenge(authChallenge.cognitoUser) });
  };

  render(): ReactNode {
    const { authChallenge } = this.state;
    if (authChallenge instanceof AcceptPoliciesChallenge) {
      return (
        <AuthLayout className="login" tip="Het gebruik van MijnTOPP vereist dat je akkoord gaat met het volgende:">
          { this.renderLoadingOverlay() }
          <AcceptPoliciesForm
            challenge={authChallenge}
            onCompleted={this.handleLogin}
            onReturn={this.handleReturn}
          />
          { this.renderErrorMessage() }
        </AuthLayout>
      );
    } else if (authChallenge instanceof NewPasswordChallenge) {
      return (
        <AuthLayout className="login" tip="Het wachtwoord van dit account moet worden geüpdatet. Voer hier een nieuw wachtwoord in.">
          { this.renderLoadingOverlay() }
          <PasswordUpdateForm
            newPasswordChallenge={authChallenge}
            onUpdated={this.handleLogin}
            onReturn={this.handleReturn}
          />
          { this.renderErrorMessage() }
        </AuthLayout>
      );
    } else if (authChallenge instanceof MfaSmsChallenge) {
      return <AuthLayout className="login" tip={`Er is zojuist een SMS verstuurd naar ${authChallenge.smsDestination}. Vul de code uit de SMS in om in te loggen.`}>
        { this.renderLoadingOverlay() }
        <MfaAuthForm
          mfaChallenge={authChallenge}
          onCompleted={this.handleLogin}
          onReturn={this.handleReturn}
        />
        { this.renderErrorMessage() }
      </AuthLayout>;
    } else if (authChallenge instanceof MfaSetupPhoneChallenge) {
      return <AuthLayout className="login" tip="Jouw account is nog niet beveiligd met tweestapsverificatie via sms-codes. Vul je mobiele telefoonnummer in om tweestapsverificatie in te stellen.">
        { this.renderLoadingOverlay() }
        <SetUserPhone
          mfaSetupPhoneChallenge={authChallenge}
          onCompleted={this.handleLogin}
          onReturn={this.handleReturn}
        />
        { this.renderErrorMessage() }
      </AuthLayout>;
    } else if (authChallenge instanceof MfaVerifyPhoneChallenge) {
      const tip = <>
        Er is een verificatie code naar je telefoonnummer gestuurd. Vul deze code in om de tweestapsverificatie af te ronden.
        <button className="login__change-phone-button" type="button" onClick={() => this.handleChangePhone(authChallenge)}>
          Of pas je telefoonnummer aan.
        </button>
      </>;
      return <AuthLayout className="login" tip={tip}>
        { this.renderLoadingOverlay() }
        <MfaAuthForm
          mfaChallenge={authChallenge}
          onCompleted={this.handleLogin}
          onReturn={this.handleReturn}
        />
        { this.renderErrorMessage() }
      </AuthLayout>;
    } else {
      return (
        <AuthLayout className="login">
          { this.renderLoadingOverlay() }
          <LoginForm onLogin={this.handleLogin} />
          { this.renderErrorMessage() }
          <Link className="login__forget-password" to={ERouterPath.passwordForgotten}>Wachtwoord vergeten?</Link>
        </AuthLayout>
      );
    }
  }
}

export default connect(
  null,
  dispatch => ({
    setActiveDossier: (dossier: DossierV2, dossierRelations: DossierMember[], relation: DossierMember) => dispatch(sessionActions.setDossier({ dossier, dossierRelations, relation })),
    clearActiveDossier: () => dispatch(sessionActions.clearDossier())
  })
)(withRouter(Login));
